import {TranslationObject} from '@colonist/i18n'
import {BigId, LobbyImage} from '@colonist/types'
import {createDivIn, createImgIn, createLinkIn, setElementInnerHTML, setElementInnerText, setElementOnClick} from '../ui_utils/DomUtils'
import {TimeUtils} from '@colonist/utils'
import {UINotificationSendHelper} from './UINotificationSendHelper'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'

export interface NotificationItemViewData {
    text: TranslationObject
    image: LobbyImage
    clickAction?: () => void
    responseButtons?: {
        acceptButtonAction: () => void
        ignoreButtonAction: () => void
    }
    secondaryLink?: {
        url: string
        text: TranslationObject
        openNewTab: boolean
    }
}

export abstract class UINotificationItemViewBase {
    protected _container: HTMLElement
    protected content: HTMLElement
    protected textContainer: HTMLElement
    protected timestamp: HTMLElement
    protected _dateReceived: Date
    protected _notificationId: BigId
    protected viewData: NotificationItemViewData
    protected unreadNotificationIcon: HTMLElement | undefined
    protected callbackOnClick: () => void

    constructor(id: BigId, dateReceived: Date, parentContainer: HTMLElement) {
        const notificationItem = createDivIn(parentContainer, 'notification_item')
        this._container = notificationItem
        this._dateReceived = dateReceived
        this._notificationId = id
    }

    protected createBaseNotification(dateReceived: Date) {
        createImgIn(this._container, this.viewData.image, 'notification_image')
        this.content = createDivIn(this._container, 'notification_content')
        this.textContainer = createDivIn(this.content, 'notification_text')
        this.timestamp = createDivIn(this.content, 'notification_timestamp')
        setElementInnerText(this.timestamp, TimeUtils.getTimeAgoTextFromDate(dateReceived))
        setElementInnerHTML(this.textContainer, this.viewData.text)
    }

    protected removeContentHoverEffectIfNoClickAction() {
        const hasViewDataClickAction = this.viewData.clickAction != undefined
        const hasMarkAsReadClickAction = this.unreadNotificationIcon != undefined
        if(hasViewDataClickAction) return
        if(hasMarkAsReadClickAction) return
        this.content.classList.toggle('notification_content_no_hover_effect', true)
    }

    protected showUnreadIconIfNeeded(dateRead: Date | undefined) {
        const isUnread = dateRead == undefined || dateRead.getTime() >= Date.now()
        const notificationHasCTAs = this.viewData.responseButtons != undefined
        const shouldShowUnreadCircle = isUnread && !notificationHasCTAs
        if(shouldShowUnreadCircle) this.unreadNotificationIcon = createDivIn(this._container, 'notification_unread_icon')
    }

    protected createResponseButtons() {
        if(this.viewData.responseButtons == undefined) return
        const responseButtonsContainer = createDivIn(this.content, 'notification_response_buttons')

        const acceptButton = createDivIn(responseButtonsContainer, 'notification_response_button btn_general')
        setElementInnerHTML(acceptButton, {key: 'strings:colonistCommon.buttons.accept'})
        setElementOnClick(acceptButton, () => {
            this.callbackOnClick()
            this.viewData.responseButtons?.acceptButtonAction()
        })

        const ignoreButton = createDivIn(responseButtonsContainer, 'notification_response_button notification_ignore_button btn_general')
        setElementInnerHTML(ignoreButton, {key: 'strings:colonistCommon.buttons.ignore'})
        setElementOnClick(ignoreButton, () => {
            this.callbackOnClick()
            this.viewData.responseButtons?.ignoreButtonAction()
        })
    }

    setCallbackOnClick(callback: () => void) {
        this.callbackOnClick = callback
    }

    protected setClickActions() {
        const notificationHasCTAs = this.viewData.responseButtons != undefined
        const contentClickAction = (e: PointerEvent) => {
            if(UIUtilsEjsVariables.isUserOnDiscord) e.preventDefault()
            if(!notificationHasCTAs) UINotificationSendHelper.sendMarkNotificationAsRead(this.notificationId)
            this.viewData.clickAction?.()
        }

        if(this.unreadNotificationIcon) {
            setElementOnClick(this.unreadNotificationIcon, () => {
                if(!notificationHasCTAs) UINotificationSendHelper.sendMarkNotificationAsRead(this.notificationId)
            })
        }

        if(this.viewData.secondaryLink == undefined) return
        const link = createLinkIn(this.content, this.viewData.secondaryLink.url, this.viewData.secondaryLink.text, 'notification_link', '', this.viewData.secondaryLink.openNewTab)
        setElementOnClick(link, contentClickAction)
    }

    get dateReceived(): Date {
        return this._dateReceived
    }

    get container(): HTMLElement {
        return this._container
    }

    get notificationId(): BigId {
        return this._notificationId
    }

    get isRead(): boolean {
        return this.unreadNotificationIcon == undefined
    }

    get hasCTAs(): boolean {
        return this.viewData.responseButtons != undefined
    }

    removeUnreadIcon() {
        if(this.unreadNotificationIcon == undefined) return
        this.unreadNotificationIcon.remove()
    }

    updateTimestamp() {
        setElementInnerText(this.timestamp, TimeUtils.getTimeAgoTextFromDate(this.dateReceived))
    }
}
